import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { filterIncludedItem } from 'utilities/hooks/responseHandlers';
import {
  generateFilterExpression,
  generateFilter,
  generateIncludeQuery,
  generatePaginationQuery,
  generateSortQuery,
  generateURL,
  generateAppendQueryParam,
} from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import type { SparkRun, SparkRunResponse } from 'types/SparkRun';
import type { Spark } from 'types/Spark/Spark';
import type {
  FilterParameters,
  PaginationQuery,
} from 'utilities/hooks/urlGeneration';
import { HistoryFilter } from 'components/History';
import { ApplicationUser } from 'types/ApplicationUser';
import { useWorkspaceStore } from 'stores';
import React from 'react';

export type SparkRunsHistoryType = {
  applicationUser: ApplicationUser | undefined;
  spark: Spark | undefined;
} & SparkRun;

const getUrl = ({
  workspaceID,
  sparkIDs = [],
  paginationQuery,
  startDate,
  endDate,
  statusFilter,
  test,
}: {
  workspaceID: string;
  sparkIDs: string[] | undefined;
  paginationQuery?: PaginationQuery;
  startDate?: string;
  endDate?: string;
  statusFilter?: string;
  test?: boolean;
}): string => {
  const andFilterConfig: FilterParameters[] = [
    {
      type: 'equal',
      key: 'workspaceID',
      value: workspaceID,
    },
  ];
  if (sparkIDs.length) {
    andFilterConfig.push({
      type: 'equal',
      key: 'sparkID',
      value: sparkIDs[0],
    });
  }
  if (statusFilter) {
    andFilterConfig.push({
      type: 'equal',
      key: 'sparkRunStatusTypeID',
      value: statusFilter,
    });
  }
  if (startDate) {
    andFilterConfig.push({
      type: 'greaterThan',
      key: 'runEnd',
      value: startDate,
    });
  }
  if (endDate) {
    andFilterConfig.push({
      type: 'lessThan',
      key: 'runEnd',
      value: endDate,
    });
  }
  if (test) {
    andFilterConfig.push({
      type: 'equal',
      key: 'test',
      value: test,
    });
  }
  const queryArray: string[] = [
    generateSortQuery('runEnd', 'Decs'),
    generateIncludeQuery(['spark', 'applicationUser']),
    ...(!!paginationQuery ? [generatePaginationQuery(paginationQuery)] : []),

    andFilterConfig.length > 1
      ? generateFilterExpression('and', andFilterConfig)
      : generateFilter('workspaceID', workspaceID),
    ...(test ? [generateAppendQueryParam('includetest', true)] : []),
  ];

  return generateURL(SPARK_RESOURCE_TYPES.SPARK_RUN, queryArray);
};

const getSparkRun = ({
  currentWorkspaceID,
  sparkIDs,
  paginationQuery,
  startDate,
  endDate,
  statusFilter,
  test,
}: {
  currentWorkspaceID: string | null | undefined;
  sparkIDs: string[] | undefined;
  paginationQuery?: PaginationQuery;
  startDate?: string;
  endDate?: string;
  statusFilter?: string;
  test?: boolean;
}) => {
  if (!currentWorkspaceID) {
    Promise.reject('workspace ID not  found');
    return;
  }
  return fetchWrapper<SparkRunResponse>({
    url: getUrl({
      workspaceID: currentWorkspaceID,
      sparkIDs,
      paginationQuery,
      startDate,
      endDate,
      statusFilter,
      test,
    }),
    method: 'GET',
  });
};

// call this with or without sparkIDs
export function useSparkRuns({
  sparkIDs,
  pagination,
  historyDateRangeFilter,
  statusFilter,
  test,
}: {
  sparkIDs?: string[];
  pagination?: PaginationQuery;
  historyDateRangeFilter?: HistoryFilter['dateRange'];
  statusFilter?: string;
  test?: boolean;
}) {
  const currentWorkspaceID = useWorkspaceStore(
    React.useCallback((state) => state.currentWorkspaceID, [])
  );
  const pageNumber = pagination?.pageNumber;
  const startDate = historyDateRangeFilter?.startDate.toISOString();
  const endDate = historyDateRangeFilter?.endDate.toISOString();

  const response = useQuery(
    [
      'sparkRun',
      sparkIDs,
      currentWorkspaceID,
      statusFilter,
      pageNumber,
      startDate,
      endDate,
      test,
    ],
    () =>
      getSparkRun({
        currentWorkspaceID,
        sparkIDs,
        paginationQuery: pagination,
        startDate,
        endDate,
        statusFilter,
        test,
      }),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Spark Run: ${error.message}`);
        }
      },
      enabled: !!currentWorkspaceID,
      ...CACHE_AND_STALE_TIME,
    }
  );

  const { data } = response;
  const { data: sparkRuns, included } = data || {};

  const sparks = filterIncludedItem<Spark>(
    included,
    SPARK_RESOURCE_TYPES.SPARKS
  );

  const applicationUsers = filterIncludedItem<ApplicationUser>(
    included,
    SPARK_RESOURCE_TYPES.APPLICATION_USERS
  );
  const getApplicationUserByID = (applicationUserID: string) => {
    return applicationUsers?.find((user) => user.id === applicationUserID);
  };

  const sparkRunsHistory: SparkRunsHistoryType[] | undefined = sparkRuns?.map(
    (sparkRun) => {
      return {
        ...sparkRun,
        applicationUser: getApplicationUserByID(sparkRun.attributes.createdBy),
        spark: sparks?.find(
          (spark) => spark.id === sparkRun.attributes.sparkID
        ),
      };
    }
  );
  const total = React.useMemo(() => {
    return data?.meta?.total;
  }, [data?.meta?.total]);

  return {
    sparkRuns,
    sparkRunsHistory,
    getApplicationUserByID,
    total,
    ...response,
  };
}
